import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, ButtonGroup, CircularProgress, Paper, Typography } from '@mui/material';
import type { Incident } from '../../services/incidentsApi';
import { incidentsApi, type IncidentReviewOutcome } from '../../services/incidentsApi';
import { sitesApi } from '../../services/sitesApi';
import ReviewerActionBar, {
  deriveReviewerCanonicalContext,
  deriveReviewerGuidance,
  deriveReviewerPrimaryAction,
} from './ReviewerActionBar';
import {
  incidentCanonicalEvidenceReadinessLabel,
  incidentCanonicalOperatorNote,
  incidentCanonicalReviewOutcomeLabel,
  incidentCanonicalEvidenceReadinessState,
} from './reviewCanonical';
import {
  deriveGroupedReviewerGuidance,
  groupedStepTitle,
  GROUPED_REVIEWER_ACTION_FOOTER_NOTE,
} from '../../domain/incidents/groupedRepresentativeAuditCopy';
import {
  OBSERVATION_DISPOSITION_ACKNOWLEDGE_HELPER,
} from '../../domain/incidents/observationDispositionCopy';
import { GROUPED_ASSIGN_ACTION_STEP_TITLES } from '../../domain/incidents/groupedAssignActionWorkflow';
import { ownerRoleIsUnassigned } from '../../domain/incidents/incidentPatternVocabulary';
import {
  PATTERN_DEFERRED_LABEL,
  PATTERN_TRIAGE_COMPLETE_LABEL,
  investigateTerminalClassificationLabel,
  isDeferredInvestigateTriage,
  isTerminalInvestigateClassification,
} from '../../domain/incidents/patternOwnershipContainment';
import type { ReviewWindowContext } from '../../domain/incidents/reviewWindowContext';
import { useAuth } from '../../contexts/AuthContext';
import { GroupedGuidedVerifyEvidenceActions } from './GroupedGuidedVerifyEvidenceActions';

export type IncidentReviewerActionPanelProps = {
  selectedIncident: Incident | null | undefined;
  canManageIncidentWorkflow: boolean;
  canCommentOnIncident: boolean;
  canUseOpsAutomation: boolean;
  followUpSignal: boolean;
  drawerIncidentLoading: boolean;
  drawerBusy: string | null;
  onAcknowledge: () => void;
  onOpenDiagnostics: () => void;
  onResolve: () => void;
  onAddComment: () => void;
  onStartReview: () => void;
  onCreateWorkOrder: () => void;
  onSyncExternal: () => void;
  groupedReviewMode?: boolean;
  observationDispositionMode?: boolean;
  selectedGroupContext?: Record<string, unknown> | null;
  reviewWindowContext?: ReviewWindowContext | null;
  followUpOwnerLabel?: string;
  resolveTargetIncidentId?: string;
  observationReviewComplete?: boolean;
  observationReviewOutcome?: string;
  observationBackToCaseLabel?: string;
  observationParentCaseIncidentId?: string;
  observationNextIncidentId?: string | null;
  onOpenParentCaseReview?: () => void;
  onOpenNextObservationReview?: () => void;
  openResolveDialogWithOutcome?: (outcome: IncidentReviewOutcome) => void;
  handleMarkFalsePositive?: () => void;
};

type CaseDecisionOption = {
  id: 'accepted' | 'rejected' | 'deferred' | 'already_managed' | 'excluded';
  label: string;
  reviewOutcome: IncidentReviewOutcome;
  closeAfter: boolean;
  deemphasizeSupporting: boolean;
};

const CASE_DECISION_OPTIONS: CaseDecisionOption[] = [
  {
    id: 'accepted',
    label: 'Accept',
    reviewOutcome: 'true_event',
    closeAfter: false,
    deemphasizeSupporting: false,
  },
  {
    id: 'rejected',
    label: 'Reject',
    reviewOutcome: 'false_positive',
    closeAfter: true,
    deemphasizeSupporting: true,
  },
  {
    id: 'deferred',
    label: 'Defer',
    reviewOutcome: 'insufficient_evidence',
    closeAfter: false,
    deemphasizeSupporting: false,
  },
  {
    id: 'already_managed',
    label: 'Already managed',
    reviewOutcome: 'expected_activity',
    closeAfter: true,
    deemphasizeSupporting: true,
  },
  {
    id: 'excluded',
    label: 'Excluded',
    reviewOutcome: 'demo_or_test_activity',
    closeAfter: true,
    deemphasizeSupporting: true,
  },
];

const CASE_FIRST_GROUPED_ROLES = new Set([
  'ehs_manager',
  'sandbox_ehs_evaluator',
  'customer_admin',
  'site_admin',
  'pilot_site_admin',
]);

function readPersistedCaseDecision(incident: Incident | null | undefined): string {
  const corrective = (incident as any)?.envelope_metadata?.corrective_action
    || (incident as any)?.safety_case?.corrective_action
    || {};
  const review = (incident as any)?.envelope_metadata?.review
    || (incident as any)?.safety_case?.review
    || {};
  return String(
    corrective?.safety_control_review?.case_decision
    || review?.case_decision
    || '',
  ).trim().toLowerCase();
}

function readGroupKey(
  incident: Incident | null | undefined,
  selectedGroupContext?: Record<string, unknown> | null,
): string {
  return String(
    (selectedGroupContext as any)?.group_key
    || (incident as any)?.group_key
    || (incident as any)?.raw?.group_key
    || (incident as any)?.safety_case?.group_key
    || '',
  ).trim();
}

function resolveCaseDecisionWindow(
  selectedGroupContext?: Record<string, unknown> | null,
  reviewWindowContext?: ReviewWindowContext | null,
): { fromDate?: string; toDate?: string } {
  const fromDate = String(
    reviewWindowContext?.fromIso
    || (selectedGroupContext as any)?.first_seen
    || (selectedGroupContext as any)?.started_at
    || '',
  ).trim();
  const toDate = String(
    reviewWindowContext?.toIso
    || (selectedGroupContext as any)?.last_seen
    || (selectedGroupContext as any)?.last_seen_at
    || '',
  ).trim();
  return {
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
  };
}

const IncidentReviewerActionPanel: React.FC<IncidentReviewerActionPanelProps> = ({
  selectedIncident,
  canManageIncidentWorkflow,
  canCommentOnIncident,
  canUseOpsAutomation,
  followUpSignal,
  drawerIncidentLoading,
  drawerBusy,
  onAcknowledge,
  onOpenDiagnostics,
  onResolve,
  onAddComment,
  onStartReview,
  onCreateWorkOrder,
  onSyncExternal,
  groupedReviewMode = false,
  observationDispositionMode = false,
  selectedGroupContext = null,
  reviewWindowContext = null,
  followUpOwnerLabel = '',
  resolveTargetIncidentId = '',
  observationReviewComplete = false,
  observationReviewOutcome = '',
  observationBackToCaseLabel = 'Back to pattern',
  observationParentCaseIncidentId = '',
  observationNextIncidentId = null,
  onOpenParentCaseReview,
  onOpenNextObservationReview,
  openResolveDialogWithOutcome,
  handleMarkFalsePositive,
}) => {
  const { effectiveUser: user } = useAuth();
  const [pilotCaseFirstMode, setPilotCaseFirstMode] = useState(false);
  const [caseDecisionBusy, setCaseDecisionBusy] = useState<string | null>(null);
  const [caseDecisionError, setCaseDecisionError] = useState<string | null>(null);
  const [caseDecisionLocalOverride, setCaseDecisionLocalOverride] = useState('');

  const selectedIncidentId = String(selectedIncident?.incident_id || '').trim();
  const selectedIncidentVersion = Number((selectedIncident as any)?.version || 0);
  const siteId = String((selectedIncident as any)?.site_id || '').trim();
  const userRole = String(user?.role || '').trim().toLowerCase();
  const roleEligible = CASE_FIRST_GROUPED_ROLES.has(userRole);

  React.useEffect(() => {
    let cancelled = false;
    if (!groupedReviewMode || !roleEligible || !siteId) {
      setPilotCaseFirstMode(false);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const complexity = await sitesApi.getComplexity(siteId);
        if (!cancelled) setPilotCaseFirstMode(Boolean(complexity?.pilot_mode));
      } catch {
        if (!cancelled) setPilotCaseFirstMode(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupedReviewMode, roleEligible, siteId]);

  React.useEffect(() => {
    setCaseDecisionLocalOverride('');
  }, [selectedIncidentId, selectedIncidentVersion]);

  const persistedCaseDecision = useMemo(
    () => caseDecisionLocalOverride || readPersistedCaseDecision(selectedIncident),
    [caseDecisionLocalOverride, selectedIncident],
  );

  const handleCaseDecision = useCallback(async (option: CaseDecisionOption) => {
    if (!selectedIncidentId) return;
    setCaseDecisionBusy(option.id);
    setCaseDecisionError(null);
    const expectedVersion = selectedIncidentVersion || 1;
    const groupKey = readGroupKey(selectedIncident, selectedGroupContext);
    const { fromDate, toDate } = resolveCaseDecisionWindow(
      selectedGroupContext,
      reviewWindowContext,
    );
    try {
      const nextStatus = option.closeAfter ? 'CLOSED' : 'IN_REVIEW';
      const updatedIncident = await incidentsApi.patchStatus(
        selectedIncidentId,
        {
          status: nextStatus,
          review_outcome: option.reviewOutcome,
          case_decision: option.id,
          ...(option.deemphasizeSupporting && groupKey ? { group_key: groupKey } : {}),
          ...(option.deemphasizeSupporting && fromDate ? { from_date: fromDate } : {}),
          ...(option.deemphasizeSupporting && toDate ? { to_date: toDate } : {}),
        },
        { expected_version: expectedVersion },
      );
      setCaseDecisionLocalOverride(readPersistedCaseDecision(updatedIncident) || option.id);
    } catch (error: any) {
      setCaseDecisionError(
        error?.response?.data?.detail
        || error?.response?.data?.error
        || error?.message
        || 'Failed to save pattern decision',
      );
    } finally {
      setCaseDecisionBusy(null);
    }
  }, [reviewWindowContext, selectedGroupContext, selectedIncident, selectedIncidentId, selectedIncidentVersion]);

  if (!selectedIncident) return null;

  const actionOwnerAssigned = !ownerRoleIsUnassigned(followUpOwnerLabel);
  const reviewerPrimary = deriveReviewerPrimaryAction({
    canManage: Boolean(canManageIncidentWorkflow),
    canCreateWorkOrder: Boolean(canUseOpsAutomation),
    status: String(selectedIncident?.status || 'OPEN'),
    hasOdooTicket: Boolean((selectedIncident as any)?.odoo_ticket_id),
    hasFollowUpSignal: followUpSignal,
  });
  const reviewerGuidance = groupedReviewMode
    ? deriveGroupedReviewerGuidance({
        primary: reviewerPrimary,
        status: String(selectedIncident?.status || 'OPEN'),
        actionOwnerAssigned,
      })
    : deriveReviewerGuidance({
        primary: reviewerPrimary,
        status: String(selectedIncident?.status || 'OPEN'),
      });
  const step5Meta = groupedStepTitle('step5', groupedReviewMode, observationDispositionMode);
  const reviewerCanonicalContext = deriveReviewerCanonicalContext({
    reviewOutcomeLabel: incidentCanonicalReviewOutcomeLabel(selectedIncident),
    evidenceReadinessLabel: incidentCanonicalEvidenceReadinessLabel(selectedIncident),
    hasOperatorNote: Boolean(incidentCanonicalOperatorNote(selectedIncident)),
  });
  const busyLabel = drawerIncidentLoading ? 'Loading latest' : drawerBusy;
  const observationStatus = String(selectedIncident?.status || 'OPEN').trim().toUpperCase();
  const showObservationAcknowledge = observationDispositionMode
    && canManageIncidentWorkflow
    && observationStatus === 'OPEN';
  const evidenceReadinessState = incidentCanonicalEvidenceReadinessState(selectedIncident);
  const evidenceAttachmentCount = Array.isArray((selectedIncident as any)?.evidence?.attachments)
    ? (selectedIncident as any).evidence.attachments.length
    : 0;
  const hasUsableObservationEvidence = Boolean(
    evidenceAttachmentCount > 0
    || String((selectedIncident as any)?.snapshot_status || '').trim().toUpperCase() === 'AVAILABLE'
    || String((selectedIncident as any)?.clip_status || '').trim().toUpperCase() === 'AVAILABLE'
    || (selectedIncident as any)?.snapshot_uri
    || (selectedIncident as any)?.clip_uri,
  );
  const observationDecisiveOutcomesBlocked = observationDispositionMode && (
    !hasUsableObservationEvidence
    || ['PENDING', 'FAILED', 'DEGRADED'].includes(evidenceReadinessState)
  );
  const observationDecisiveOutcomesBlockReason = observationDispositionMode
    ? !hasUsableObservationEvidence
      ? 'No usable snapshot or clip is available for this observation yet. Record Needs more review until evidence is available.'
      : evidenceReadinessState === 'PENDING'
        ? 'Evidence for this observation is still being prepared. Record Needs more review until a usable snapshot or clip is available.'
        : evidenceReadinessState === 'FAILED'
          ? 'Evidence for this observation failed to load. Record Needs more review until a usable snapshot or clip is available.'
          : evidenceReadinessState === 'DEGRADED'
            ? 'Evidence for this observation is degraded. Record Needs more review until a usable snapshot or clip is available.'
            : ''
    : '';

  const showCaseDecisionRail = groupedReviewMode
    && pilotCaseFirstMode
    && canManageIncidentWorkflow;

  const dispositionActionScope = {
    canManageIncidentWorkflow,
    drawerBusy,
    resolveTargetIncidentId,
    groupedRepresentativeReviewComplete: observationReviewComplete,
    groupedRepresentativeReviewOutcome: observationReviewOutcome,
    observationDispositionMode: true,
    observationDecisiveOutcomesBlocked,
    observationDecisiveOutcomesBlockReason,
    observationBackToCaseLabel,
    observationParentCaseIncidentId,
    observationNextIncidentId,
    onOpenParentCaseReview,
    onOpenNextObservationReview,
    openResolveDialogWithOutcome,
    handleMarkFalsePositive,
  };

  return (
    <>
      {showCaseDecisionRail ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
            Pattern decision
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            One persisted pattern decision drives workflow for this recurring risk pattern.
          </Typography>
          {caseDecisionError ? (
            <Alert severity="error" sx={{ mb: 1.25 }}>
              {caseDecisionError}
            </Alert>
          ) : null}
          {persistedCaseDecision ? (
            <Typography
              variant="body2"
              sx={{ mb: 1.25 }}
              data-testid={
                isTerminalInvestigateClassification({ caseDecision: persistedCaseDecision })
                  ? 'investigate-triage-complete'
                  : isDeferredInvestigateTriage({ caseDecision: persistedCaseDecision })
                    ? 'investigate-triage-deferred'
                    : 'investigate-saved-decision'
              }
            >
              {isTerminalInvestigateClassification({ caseDecision: persistedCaseDecision })
                ? `${PATTERN_TRIAGE_COMPLETE_LABEL} · ${investigateTerminalClassificationLabel({ caseDecision: persistedCaseDecision })}`
                : isDeferredInvestigateTriage({ caseDecision: persistedCaseDecision })
                  ? `${PATTERN_DEFERRED_LABEL} — follow-up review is still required.`
                  : (
                    <>
                      Saved decision:{' '}
                      <strong>
                        {CASE_DECISION_OPTIONS.find((item) => item.id === persistedCaseDecision)?.label
                          || persistedCaseDecision}
                      </strong>
                    </>
                  )}
            </Typography>
          ) : null}
          <ButtonGroup
            size="small"
            variant="outlined"
            aria-label="Pattern decision actions"
            disabled={Boolean(drawerIncidentLoading || drawerBusy || caseDecisionBusy)}
          >
            {CASE_DECISION_OPTIONS.map((option) => (
              <Button
                key={option.id}
                onClick={() => void handleCaseDecision(option)}
                color={persistedCaseDecision === option.id ? 'primary' : 'inherit'}
                variant={persistedCaseDecision === option.id ? 'contained' : 'outlined'}
              >
                {caseDecisionBusy === option.id ? (
                  <CircularProgress size={16} sx={{ mr: 0.75 }} />
                ) : null}
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      ) : null}
      {observationDispositionMode ? (
        <>
          <Paper
            variant="outlined"
            sx={{ p: 1.75, mb: 1.25, borderRadius: 3, borderColor: '#e4e7ec' }}
            data-testid="observation-disposition-panel"
          >
            <Typography sx={{ fontSize: 15, fontWeight: 800, mb: step5Meta.subtitle ? 0.35 : 0.5 }}>
              {step5Meta.title}
            </Typography>
            {step5Meta.subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, lineHeight: 1.45 }}>
                {step5Meta.subtitle}
              </Typography>
            ) : null}
            <GroupedGuidedVerifyEvidenceActions scope={dispositionActionScope as any} />
            {busyLabel ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  {busyLabel}…
                </Typography>
              </Box>
            ) : null}
          </Paper>
          <Box
            data-testid="observation-acknowledge-helper"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
              {OBSERVATION_DISPOSITION_ACKNOWLEDGE_HELPER}
            </Typography>
            {showObservationAcknowledge ? (
              <Button size="small" variant="text" onClick={onAcknowledge} sx={{ fontWeight: 700 }}>
                Acknowledge
              </Button>
            ) : null}
          </Box>
        </>
      ) : null}
      {!observationDispositionMode ? (
        <ReviewerActionBar
        canManageIncidentWorkflow={canManageIncidentWorkflow}
        canOpenDiagnostics={Boolean(selectedIncident?.device_id)}
        canAddComment={canCommentOnIncident}
        canResolve={canManageIncidentWorkflow}
        canCreateWorkOrder={canUseOpsAutomation}
        canSyncExternal={canUseOpsAutomation}
        primaryAction={reviewerPrimary}
        guidance={reviewerGuidance}
        canonicalContext={reviewerCanonicalContext}
        busyLabel={busyLabel}
        onAcknowledge={onAcknowledge}
        onOpenDiagnostics={onOpenDiagnostics}
        onResolve={onResolve}
        onDismissOrClose={onResolve}
        onAddComment={onAddComment}
        onStartReview={onStartReview}
        onCreateWorkOrder={onCreateWorkOrder}
        onSyncExternal={onSyncExternal}
        incidentWorkflowStatus={String(selectedIncident?.status || 'OPEN')}
        groupedReviewMode={groupedReviewMode}
        observationDispositionMode={observationDispositionMode}
        headingTitle={
          groupedReviewMode
            ? GROUPED_ASSIGN_ACTION_STEP_TITLES.acknowledge
            : undefined
        }
        headingSubtitle={undefined}
        footerNote={
          groupedReviewMode
            ? GROUPED_REVIEWER_ACTION_FOOTER_NOTE
            : undefined
        }
        assignActionSectionsLocked={groupedReviewMode && !actionOwnerAssigned}
      />
      ) : null}
    </>
  );
};

export default IncidentReviewerActionPanel;
